import React, { type JSX } from "react";
import { useStreamingMessage } from "../../hooks/useStreamingMessage";
import { getDocumentColor, factIdsInText } from "./chatbotConstants";

const DOCUMENT_ICON_PATH =
  "M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zm2.25 8.5a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm0 3a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z";

interface FooterDocument {
  documentId: string;
  title?: string;
  documentName?: string;
  originalFilename?: string;
}

interface ChatFactFooterProps {
  message: { text?: string; turnId?: string } | null;
  onDocumentClick: (document: any) => void;
  allFacts: Array<{ factId: string; documentId?: string }>;
  allDocuments: FooterDocument[];
}

/** The documents whose facts are cited in this message, in registry order. */
function citedDocuments(
  messageText: string,
  allFacts: Array<{ factId: string; documentId?: string }>,
  allDocuments: FooterDocument[]
): FooterDocument[] {
  const factIdsInMessage = factIdsInText(messageText);
  if (factIdsInMessage.size === 0) {
    return [];
  }

  // Collect unique document IDs from facts referenced in this message
  const docIds = new Set(
    allFacts
      .filter((f) => factIdsInMessage.has(f.factId))
      .map((f) => f.documentId)
      .filter(Boolean)
  );

  // Resolve full document objects from central documents array
  return allDocuments.filter((doc) => docIds.has(doc.documentId));
}

/** One colour-coded source button. */
function SourceButton({
  document,
  docIndex,
  onDocumentClick,
}: {
  document: FooterDocument;
  docIndex: number;
  onDocumentClick: (document: any) => void;
}): JSX.Element {
  const colors = getDocumentColor(docIndex);
  // Backend normalizes originalFilename to title field
  const title = document.title || document.documentName || `Document ${docIndex}`;

  return (
    <button
      onClick={() => onDocumentClick(document)}
      title={title}
      className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs ${colors.bg} border ${colors.border} hover:opacity-80 transition-opacity w-full text-left group`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className={`h-4 w-4 shrink-0 ${colors.text}`}
      >
        <path fillRule="evenodd" d={DOCUMENT_ICON_PATH} clipRule="evenodd" />
      </svg>
      <span className={`truncate font-medium ${colors.text}`}>{title}</span>
    </button>
  );
}

/**
 * Chat fact footer - shows color-coded document list for a message
 * Phase 8: Reads text from streaming state when available, falls back to message.text
 */
export default function ChatFactFooter({
  message,
  onDocumentClick,
  allFacts,
  allDocuments,
}: ChatFactFooterProps): JSX.Element | null {
  // Phase 8: Use streaming hook to get the current text (whether streaming or finalized)
  const streamingData = useStreamingMessage(message?.turnId || "");

  if (!message || !allFacts || !allDocuments) {
    return null;
  }

  // During streaming: streamingData.displayedText has the animated text
  // After finalization: message.text has the final persisted text
  const messageText = streamingData.displayedText || message.text || "";
  const turnDocs = citedDocuments(messageText, allFacts, allDocuments);

  if (turnDocs.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 pt-2 border-t border-cvc-grey">
      <div className="text-xs text-cvc-text/70 mb-1.5 font-cvcBody">Sources referenced</div>
      <div className="flex flex-col gap-1.5">
        {turnDocs.map((document, index) => (
          <SourceButton
            key={document.documentId || index}
            document={document}
            docIndex={index + 1}
            onDocumentClick={onDocumentClick}
          />
        ))}
      </div>
    </div>
  );
}
